/**
 * Checkbox component — shared types and Zod prop schema.
 *
 * A square checkbox box with an optional visible label. When `label` is set it
 * renders beside the box inside the same control, so pressing the text toggles
 * the box and assistive technology meets one checkbox, not a box plus a
 * separate sentence. Without a label the consumer renders any row text.
 * Unchecked: a bordered white square; checked: a filled dark square with a
 * tick glyph; `error` swaps the border for the alert stroke.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import type { TextColor, TextVariant } from '../Text/Text';
import type { IconName } from '../../elements/icons/registry';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const checkboxPropsSchema = z.object({
  checked: z.boolean().default(false),
  disabled: z.boolean().default(false),
  accessibilityLabel: z.string(),
  /** Visible text beside the box; pressing it toggles the box. */
  label: z.string().optional(),
  /** Marks the box as needing attention (alert border), e.g. a required tick left empty. */
  error: z.boolean().default(false),
});

export type CheckboxProps = z.infer<typeof checkboxPropsSchema>;

/** Box edge length — Figma 24×24. */
export const CHECKBOX_SIZE = 24;

/** Border width of the unchecked box. */
export const CHECKBOX_BORDER_WIDTH = 1;

// Figma rounds the box with `spacing/s` (8px); the matching radius token is
// `radius.m` (8). (The DS expresses Figma's spacing-valued corners through the
// radius token of equal value — see TextInput's 16px corner → `radius.xl`.)
export const CHECKBOX_RADIUS = radius.m;

// The bare checkmark (Figma "Stroke 1"), the same glyph the radio's
// tick-with-container cuts out — so checkbox and radio checks read identically.
export const CHECKBOX_TICK_ICON: IconName = 'check';

// The glyph is framed in the full 24×24 box, so it fills the box to land at the
// Figma insets (rather than a smaller centered mark).
export const CHECKBOX_TICK_SIZE = CHECKBOX_SIZE;

// Expands the 24px box to a ≥44×44 touch target (ADR-025 Rule 5). Derived from
// the 48px minimum touch target so there are no raw size literals: (48-24)/2.
export const CHECKBOX_HIT_SLOP = (sizing.touchTarget.minimum - CHECKBOX_SIZE) / 2;

// `stroke.input` keeps the unticked box's boundary at about 3.7:1 on white
// (WCAG 1.4.11); `stroke.divider` was about 1.3:1 and the box read as blank.
export const CHECKBOX_BORDER_COLOR = colors.stroke.input;
export const CHECKBOX_ERROR_BORDER_COLOR = colors.stroke.alert;
export const CHECKBOX_BACKGROUND_UNCHECKED = colors.background.container;
export const CHECKBOX_BACKGROUND_CHECKED = colors.button.primary;
export const CHECKBOX_TICK_COLOR = colors.icon.inverted;

export const CHECKBOX_LABEL_GAP = spacing.m;
// A labelled row carries no hit slop (it would reach the content below), so the
// row itself holds the touch minimum on both axes, even for a short or empty label.
export const CHECKBOX_LABELLED_MIN_HEIGHT = sizing.touchTarget.minimum;
// The label reads as the body sentence it replaces beside the box.
export const CHECKBOX_LABEL_VARIANT: TextVariant = 'b3Regular';
export const CHECKBOX_LABEL_COLOR: TextColor = 'primary';

/**
 * Box fill + border for a given checked state. The checked box's border matches
 * its dark fill (Figma `button/primary`) so the filled square reads as one solid
 * shape — no contrasting grey ring. Unchecked keeps the resting input border.
 * `error` keeps the fill and swaps the border for the alert stroke.
 */
export function checkboxBoxColorsFor(
  checked: boolean,
  error = false,
): { background: string; border: string } {
  const background = checked ? CHECKBOX_BACKGROUND_CHECKED : CHECKBOX_BACKGROUND_UNCHECKED;
  if (error) return { background, border: CHECKBOX_ERROR_BORDER_COLOR };
  return { background, border: checked ? CHECKBOX_BACKGROUND_CHECKED : CHECKBOX_BORDER_COLOR };
}
